import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { enhancedApi } from '../api';
import AddTeamForm from './AddTeamForm';
import TeamClientAssignments from './TeamClientAssignments'; // Import the new component

const TeamsTab = () => {
  const [teams, setTeams] = useState([]);
  const [openTeamDialog, setOpenTeamDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false); // New state for Assign dialog
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTeam, setMenuTeam] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await enhancedApi.getTeams();
      const data = response.data;
      setTeams(Array.isArray(data) ? data : (data?.data || []));
      setError('');
    } catch (error) {
      console.error('Error fetching teams:', error);
      setError('Failed to load teams');
    }
  };

  const handleOpenTeamDialog = (team = null) => {
    setSelectedTeam(team);
    setOpenTeamDialog(true);
  };

  const handleCloseTeamDialog = () => {
    setOpenTeamDialog(false);
    setSelectedTeam(null);
  };

  const handleTeamSubmit = () => {
    setOpenTeamDialog(false);
    setSelectedTeam(null);
    fetchTeams();
  };

  const handleOpenViewDialog = (team) => {
    setSelectedTeam(team);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedTeam(null);
  };

  const handleOpenAssignDialog = (team) => {
    setSelectedTeam(team);
    setOpenAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
    setSelectedTeam(null);
    fetchTeams(); // Refresh teams to update any changes
  };

  const handleMenuClick = (event, team) => {
    setAnchorEl(event.currentTarget);
    setMenuTeam(team);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTeam(null);
  };

  const handleMenuAction = (action) => {
    if (!menuTeam) return;

    switch (action) {
      case 'view':
        handleOpenViewDialog(menuTeam);
        break;
      case 'edit':
        handleOpenTeamDialog(menuTeam);
        break;
      case 'assign':
        handleOpenAssignDialog(menuTeam);
        break;
      case 'delete':
        console.log('Delete Team:', menuTeam);
        // Placeholder for Delete Team (implement as needed)
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="primary" onClick={() => handleOpenTeamDialog()}>
          Create New Team
        </Button>
      </Box>

      {error && <Typography variant="body1" color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Team Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Assigned Users</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teams.length > 0 ? (
            teams.map(team => (
              <TableRow key={team.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {team.icon_color && (
                      <Box sx={{ width: 16, height: 16, backgroundColor: team.icon_color, mr: 1 }} />
                    )}
                    {team.name}
                  </Box>
                </TableCell>
                <TableCell>{team.description || '—'}</TableCell>
                <TableCell>{Array.isArray(team.users) && team.users.length > 0 ? team.users.join(', ') : '—'}</TableCell>
                <TableCell>
                  <IconButton onClick={(event) => handleMenuClick(event, team)}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && menuTeam === team}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleMenuAction('view')}>View Team</MenuItem>
                    <MenuItem onClick={() => handleMenuAction('edit')}>Edit Team</MenuItem>
                    <MenuItem onClick={() => handleMenuAction('assign')}>Assign to Client</MenuItem>
                    <MenuItem onClick={() => handleMenuAction('delete')}>Delete Team</MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body1" align="center">No teams found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={openTeamDialog} onClose={handleCloseTeamDialog} fullWidth maxWidth="sm">
        <AddTeamForm
          open={openTeamDialog}
          onClose={handleCloseTeamDialog}
          onTeamSubmit={handleTeamSubmit}
          teamToEdit={selectedTeam}
        />
      </Dialog>

      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} fullWidth maxWidth="sm">
        <DialogTitle>View Team: {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Name:</strong> {selectedTeam?.name}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Description:</strong> {selectedTeam?.description || '—'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <strong>Icon Color:</strong>
            {selectedTeam?.icon_color ? (
              <Box sx={{ width: 20, height: 20, backgroundColor: selectedTeam.icon_color, ml: 1 }} />
            ) : (
              <Typography sx={{ ml: 1 }}>—</Typography>
            )}
          </Box>
          <Typography variant="body1">
            <strong>Assigned Users:</strong> {Array.isArray(selectedTeam?.users) && selectedTeam.users.length > 0 ? selectedTeam.users.join(', ') : '—'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <TeamClientAssignments
        teamId={selectedTeam?.id}
        open={openAssignDialog}
        onClose={handleCloseAssignDialog}
      />
    </Box>
  );
};

export default TeamsTab;